import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, createUserAlias } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ApiException, User } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import axios from 'axios';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface AddUserAliasDialogProps {
  open: boolean;
  handleClose: () => void;
  domains: string[];
  user: User;
  changeLogRoles: string[];
}

export default function AddUserAliasDialog({
  open,
  handleClose,
  domains,
  user,
  changeLogRoles,
}: AddUserAliasDialogProps) {
  const queryClient = useQueryClient();
  const { primaryDomain } = useUserSettings();
  const { createEvent } = useAnalytics();
  const [userName, setUserName] = useState('');
  const [hasError, setHasError] = useState(false);
  const [domain, setDomain] = useState(primaryDomain);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      createUserAlias(
        user.primaryEmail,
        `${userName}@${domain}`,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User alias created successfully!',
      });

      const currentAliases = user.aliases || [];

      queryClient.setQueryData([UserKeys.User, user.primaryEmail], {
        ...user,
        aliases: [`${userName}@${domain}`, ...currentAliases],
      });

      setUserName('');
      setDomain(primaryDomain);
      setHasError(false);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message?.includes('409')) {
          setHasError(true);
        } else {
          handleErrors({
            error,
            snackbarMessage:
              'There was an issue creating a user alias. Please refresh and try again.',
          });
        }
      }
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.aliases.create_user_alias,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.aliases,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.AddAliases.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setUserName('');
        setHasError(false);
        setDomain(primaryDomain);
        handleClose();
      }}
      title="Add Alias"
      dialogContent={
        <Stack gap={2}>
          <Typography>
            Add alias for <strong>{user.primaryEmail}</strong>.
          </Typography>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <TextField
              autoFocus
              id="alias"
              error={hasError}
              helperText={hasError ? 'Alias already exists.' : ''}
              label="Alias"
              placeholder="Enter alias"
              fullWidth
              autoComplete="off"
              value={userName}
              onChange={(event) => setUserName(event.target.value)}
            />
            <Typography>@</Typography>

            <TextField
              select
              error={hasError}
              fullWidth
              value={domain}
              onChange={(event) => setDomain(event.target.value)}
            >
              {domains.map((domain) => (
                <MenuItem value={domain} key={domain}>
                  {domain}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Add Alias',
        action: handleSubmit,
        loading: isPending,
        disabled: userName === '',
      }}
    />
  );
}
